import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useAsyncPaginatedData } from "../../../hooks/useAsyncPaginatedData";
import { usePageView } from "../../../hooks/usePageView";
import { useUserContext } from "../../../context/UserContext";
import {
  listInsights,
  withdrawInsight,
  deleteInsight,
  INSIGHT_LIST_MAX_LIMIT,
} from "../../../lib/expert_insights_api";
import type { ExpertInsightSummary } from "../../../lib/expert_insights_api";
import { ROUTES } from "../../../route_paths";

const PAGE_SIZE = 20;

function resolveTitle(insight: ExpertInsightSummary): string {
  return insight.draft?.title ?? insight.currentVersion?.title ?? "";
}

function resolveBody(insight: ExpertInsightSummary): string {
  return insight.draft?.body ?? insight.currentVersion?.body ?? "";
}

function matchesQuery(insight: ExpertInsightSummary, lower: string): boolean {
  return (
    resolveTitle(insight).toLowerCase().includes(lower) ||
    resolveBody(insight).toLowerCase().includes(lower)
  );
}

export interface UseInsightListResult {
  paginatedInsights: ExpertInsightSummary[];
  totalPages: number;
  page: number;
  query: string;
  fieldFilter: string | null;
  authorizedFields: string[];
  /** True when the profile has loaded and the expert has no authorized fields — a setup error. */
  noAuthorizedFields: boolean;
  loading: boolean;
  error: string | null;
  setQuery: (query: string) => void;
  setFieldFilter: (field: string | null) => void;
  setPage: (page: number) => void;
  handleNew: () => void;
  handleEdit: (id: string) => void;
  handleWithdraw: (id: string) => Promise<void>;
  handleDeleteDraft: (id: string) => Promise<void>;
}

/** Fetches, searches, paginates, and field-filters the contributor's insights. */
export function useInsightList(): UseInsightListResult {
  usePageView("expert-insights/list");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, loading: profileLoading, profile } = useUserContext();
  const userId = session?.user?.id ?? null;

  const [query, setQuery] = useState("");
  const [fieldFilter, setFieldFilter] = useState<string | null>(null);
  const debouncedQuery = useDebouncedValue(query);

  const cacheKey = `${debouncedQuery}|${fieldFilter ?? ""}`;

  const {
    items: paginatedInsights,
    totalPages,
    page,
    loading,
    error: listError,
    goToPage,
    refresh,
  } = useAsyncPaginatedData(
    (offsetPage) =>
      listInsights({
        q: debouncedQuery || undefined,
        field: fieldFilter ?? undefined,
        ...offsetPage,
      }),
    {
      pageSize: PAGE_SIZE,
      prefetchLimit: INSIGHT_LIST_MAX_LIMIT,
      localFilter: (insight) => {
        if (query && !matchesQuery(insight, query.toLowerCase())) return false;
        if (fieldFilter && !insight.fields.includes(fieldFilter)) return false;
        return true;
      },
      key: cacheKey,
      prefetchCacheKey: userId ? `rc_insights_${userId}_${fieldFilter ?? ""}` : undefined,
    }
  );

  const noProfile = !profileLoading && profile === null;
  const error = noProfile ? t("expertInsights.insights.noProfile") : listError;
  const authorizedFields = profile?.fields ?? [];

  return {
    paginatedInsights,
    totalPages,
    page,
    query,
    fieldFilter,
    authorizedFields,
    noAuthorizedFields: !profileLoading && profile !== null && authorizedFields.length === 0,
    loading,
    error,
    setQuery,
    setFieldFilter,
    setPage: goToPage,
    handleNew: () => navigate(ROUTES.expertInsights.insights.new.path()),
    handleEdit: (id) => navigate(ROUTES.expertInsights.insights.edit.path({ id })),
    handleWithdraw: async (id) => {
      await withdrawInsight(id);
      await refresh();
    },
    handleDeleteDraft: async (id) => {
      await deleteInsight(id);
      await refresh();
    },
  };
}
